import { useEffect, useId, useRef, useState } from "react";
import type { FeedbackReport } from "@seedance/contracts";
import { adminClient } from "./admin-client.js";
import { copyFeedbackText } from "./feedback-state.js";
export function FeedbackPreview({ caseId, onError }: { caseId: string; onError(error: unknown): void }) {
  const [includePrompt, setPrompt] = useState(false), [includeMaterialList, setMaterials] = useState(false);
  const [report, setReport] = useState<FeedbackReport>(), [notice, setNotice] = useState(""); const [busy, setBusy] = useState(false);
  const gate = useRef(false), text = useRef<HTMLTextAreaElement>(null), alive = useRef(true);
  const previewId = useId();
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  function invalidate() { setReport(undefined); setNotice(""); }
  async function preview() {
    if (gate.current) return; gate.current = true; setBusy(true); invalidate();
    try { const result = await adminClient.previewFeedback(caseId, { includePrompt, includeMaterialList }); if (alive.current) setReport(result); }
    catch (error) { if (alive.current) onError(error); } finally { gate.current = false; if (alive.current) setBusy(false); }
  }
  async function copy() {
    if (!report || gate.current) return; gate.current = true; setBusy(true);
    try {
      // Recheck authorization before making a retained preview available to the clipboard.
      await adminClient.getCase(caseId);
      if (!alive.current) return;
      const result = await copyFeedbackText(report.text, navigator.clipboard);
      setNotice(result === "copied" ? "已复制；尚未发送给 43，也未改变进度。" : "无法自动复制，请选中下方只读文本手动复制；尚未发送。 ");
      if (result === "manual") { text.current?.focus(); text.current?.select(); }
    } catch (error) { if (alive.current) { invalidate(); onError(error); } } finally { gate.current = false; if (alive.current) setBusy(false); }
  }
  return <section className="admin-panel admin-card" aria-label="反馈说明预览">
    <h2>预览给 43 的反馈说明</h2><p className="admin-muted">仅准备说明，不自动发消息、不申请退款、不重试生成。</p>
    <fieldset disabled={busy} className="admin-fields"><legend>默认不包含公司内容</legend>
      <label className="admin-check"><input type="checkbox" checked={includePrompt} onChange={e => { setPrompt(e.target.checked); invalidate(); }} />加入公司提示词</label>
      <label className="admin-check"><input type="checkbox" checked={includeMaterialList} onChange={e => { setMaterials(e.target.checked); invalidate(); }} />加入素材类型与用途清单（不含链接）</label>
    </fieldset>
    {(includePrompt || includeMaterialList) && <p className="admin-warning">将包含公司内容。请确认有权向 43 提供，并逐字检查下方新预览。</p>}
    <button className="admin-button" disabled={busy} onClick={() => void preview()}>{busy ? "正在处理…" : "生成完整预览"}</button>
    {report && <><div className="admin-preview"><label htmlFor={previewId}>完整反馈说明</label><textarea id={previewId} ref={text} readOnly value={report.text} rows={16} /></div>
      {report.missingFields.length > 0 && <p className="admin-muted">未获取：{report.missingFields.join("、")}。没有使用本地编号替代上游编号。</p>}
      <button className="admin-button admin-button--primary" disabled={busy} onClick={() => void copy()}>复制预览内容</button></>}
    {notice && <p role="status" aria-label="复制结果">{notice}</p>}
  </section>;
}
